import type { IntlShape } from '@atlassian/jira-intl';
import { SECONDS_PER_HOUR } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { roundTo2DecimalPlaces } from '../round-to-2-decimals';

export const ESTIMATION_STORY_POINTS = 'STORY_POINTS' as const;
export const ESTIMATION_ISSUE_COUNT = 'ISSUE_COUNT' as const;
export const ESTIMATION_ORIGINAL_ESTIMATE = 'ORIGINAL_ESTIMATE' as const;

export type EstimationStatistic =
	| typeof ESTIMATION_STORY_POINTS
	| typeof ESTIMATION_ISSUE_COUNT
	| typeof ESTIMATION_ORIGINAL_ESTIMATE;

/**
 * Formats the estimate according to the estimationStatistic, including rounding the value to 2 decimals if applicable,
 * or converting the value to respect the time tracking options if the estimation statistic is 'ORIGINAL_ESTIMATE' (value should be in seconds).
 */
export const formatEstimate = (
	estimate: number,
	timeTrackingOptions: TimeTrackingOptions,
	estimationStatistic: EstimationStatistic | null,
	intl: IntlShape,
): string => {
	if (estimationStatistic === ESTIMATION_ORIGINAL_ESTIMATE) {
		return timeTrackingFormatter(estimate, timeTrackingOptions, intl);
	}
	if (estimationStatistic === ESTIMATION_STORY_POINTS) {
		return String(roundTo2DecimalPlaces(estimate));
	}
	return String(estimate);
};

/**
 * Formats the estimate according to the estimation statistic, including rounding the value to 2 decimals if applicable,
 * or converting the value to respect the time tracking options if the estimation statistic is 'ORIGINAL_ESTIMATE' (value should be in hours as the y axis is hours).
 */
export const formatEstimateForTooltip = (
	estimate: number,
	timeTrackingOptions: TimeTrackingOptions,
	estimationStatistic: EstimationStatistic | null,
	intl: IntlShape,
): string => {
	const estimateToUse =
		estimationStatistic === ESTIMATION_ORIGINAL_ESTIMATE ? estimate * SECONDS_PER_HOUR : estimate;
	return formatEstimate(estimateToUse, timeTrackingOptions, estimationStatistic, intl);
};
